import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getUserBooks, deleteBook } from '../services/bookService';
import { formatDistanceToNow } from 'date-fns';
import { TrashIcon, PhotoIcon } from '@heroicons/react/24/outline';

export default function LibraryPage() {
  const [books, setBooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadBooks = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const userBooks = await getUserBooks();
      setBooks(userBooks);
    } catch (err) {
      console.error('Failed to load books:', err);
      setError(err.message || 'Failed to load your books. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBooks();
  }, []);

  const handleDeleteBook = async (bookId) => {
    if (!window.confirm('Are you sure you want to delete this book?')) {
      return;
    }

    try {
      await deleteBook(bookId);
      setBooks(prevBooks => prevBooks.filter(book => book.id !== bookId));
    } catch (err) {
      console.error('Failed to delete book:', err);
      alert('Failed to delete book. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-700">{error}</p>
        <button
          onClick={loadBooks}
          className="mt-4 text-indigo-600 hover:text-indigo-500"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className={`flex items-center mb-6 ${books.length > 0 ? 'justify-between' : 'justify-center'}`}>
        <h1 className={`text-2xl font-bold text-gray-900 ${books.length === 0 ? 'text-center' : ''}`}>My Library</h1>
        {books.length > 0 && (
          <Link
            to="/create"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Create New Book
          </Link>
        )}
      </div>

      {books.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-6">You haven't created any books yet.</p>
          <Link
            to="/create"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Create New Book
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {books.map(book => (
            <div key={book.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="w-full bg-gradient-to-br from-gray-100 to-gray-200 px-6" style={{ aspectRatio: '3/2' }}>
                {book.coverImage ? (
                  <img 
                    src={book.coverImage} 
                    alt={book.title} 
                    className="object-contain w-full h-full"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-2">
                    <PhotoIcon className="h-12 w-12 text-gray-400" />
                    <span className="text-sm text-gray-500 font-medium">No cover image yet</span>
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{book.title}</h3>
                <p className="text-sm text-gray-500 mb-2">
                  {book.createdAt ? 
                    formatDistanceToNow(book.createdAt, { addSuffix: true }) :
                    'Recently'
                  }
                </p>
                <div className="flex justify-between items-center">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    {book.status}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleDeleteBook(book.id)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete book"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                    <Link
                      to={book.status === 'draft' ? `/edit/${book.id}` : `/design/${book.id}`}
                      className="text-sm text-indigo-600 hover:text-indigo-900"
                    >
                      Continue editing
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}